import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, addDoc, serverTimestamp, deleteDoc, doc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import FormulaireVisite from './FormulaireVisite';
import CarteClients from './CarteClients';
import PlanningSemaine from './PlanningSemaine';
import ProfilCommercial from './ProfilCommercial';
import SelecteurTheme from './SelecteurTheme';
import { useTheme } from '../ThemeContext';
import RechercheGlobale from './RechercheGlobale';
import FicheClient from './FicheClient';
import Notifications from './Notifications';
import { creerNotification } from './Notifications';
import { TrackingWidget } from './TrackingGPS';
import { useLang, LangToggle } from '../LanguageContext';
import { t } from '../i18n';

const COULEUR_DEFAULT = '#C0392B';

export default function CommercialHome({ user, onLogout }) {
  const { theme } = useTheme();
  const { lang, isRTL } = useLang();
  const [showTheme, setShowTheme] = useState(false);
  const [showRecherche, setShowRecherche] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [nbNotifs, setNbNotifs] = useState(0);
  const [ficheClient, setFicheClient] = useState(null);
  const [vue, setVue] = useState('accueil');
  const [widget, setWidget] = useState(null);
  const [visites, setVisites] = useState([]);
  const [clients, setClients] = useState([]);
  const [actions, setActions] = useState([]);
  const [wilaya, setWilaya] = useState('Constantine');
  const [clientSelectionne, setClientSelectionne] = useState(null);
  const [showFormVisite, setShowFormVisite] = useState(false);
  const [showFormClient, setShowFormClient] = useState(false);
  const [nouveauClient, setNouveauClient] = useState({ nom: '', telephone: '', commune: '', wilaya: 'Constantine', type: 'GROSSISTE', statut: 'Prospect', potentiel: 'Moyen', gpsText: '' });
  const [loadingClient, setLoadingClient] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [visiteASupprimer, setVisiteASupprimer] = useState(null);

  useEffect(() => { chargerDonnees(); detecterWilaya(); chargerNbNotifs(); }, []);

  const chargerNbNotifs = async () => {
    try {
      const { getDocs, query, collection, where } = await import('firebase/firestore');
      const snap = await getDocs(query(collection(db, 'notifications'), where('destinataireId', '==', user.uid), where('lu', '==', false)));
      setNbNotifs(snap.size);
    } catch(e) {}
  };

  const chargerDonnees = async () => {
    try {
      const vSnap = await getDocs(query(collection(db, 'visites'), where('commercialId', '==', user.uid)));
      const vs = vSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      vs.sort((a, b) => new Date(b.heureDebut || 0) - new Date(a.heureDebut || 0));
      setVisites(vs);
      const cSnap = await getDocs(query(collection(db, 'clients'), where('commercialIds', 'array-contains', user.uid)));
      setClients(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const aSnap = await getDocs(query(collection(db, 'actions'), where('commercialId', '==', user.uid), where('statut', '==', 'A FAIRE')));
      setActions(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.log('Erreur:', e); }
  };

  const detecterWilaya = () => {
    navigator.geolocation?.getCurrentPosition(async pos => {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`);
        const data = await res.json();
        setWilaya(data.address?.state || data.address?.city || 'Constantine');
      } catch { setWilaya('Constantine'); }
    });
  };

  const ajouterClient = async () => {
    if (!nouveauClient.nom.trim()) return;
    setLoadingClient(true);
    try {
      let gps = null;
      await new Promise(resolve => {
        navigator.geolocation?.getCurrentPosition(pos => { gps = { lat: pos.coords.latitude, lng: pos.coords.longitude }; resolve(); }, resolve);
      });
      const { gpsText, ...clientData } = nouveauClient;
      await addDoc(collection(db, 'clients'), {
        ...clientData,
        commercialIds: [user.uid],
        gps,
        createdAt: serverTimestamp(),
      });
      setShowFormClient(false);
      setNouveauClient({ nom: '', telephone: '', commune: '', wilaya: 'Constantine', type: 'GROSSISTE', statut: 'Prospect', potentiel: 'Moyen', gpsText: '' });
      chargerDonnees();
    } catch (e) { alert('Erreur: ' + e.message); }
    setLoadingClient(false);
  };

  const supprimerVisite = async (visiteId) => {
    try {
      await deleteDoc(doc(db, 'visites', visiteId));
      setVisiteASupprimer(null);
      chargerDonnees();
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  const handleLogout = async () => { await signOut(auth); onLogout(); };

  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' });
  const visitesToday = visites.filter(v => {
    if (!v.heureDebut) return false;
    return new Date(v.heureDebut).toDateString() === new Date().toDateString();
  });
  const actionsRetard = actions.filter(a => a.dateAction && new Date(a.dateAction) < new Date());
  const actionsAvenir = actions.filter(a => a.dateAction && new Date(a.dateAction) >= new Date());
  const score = Math.round((visites.length / Math.max(user.objectifVisites || 60, 1)) * 100);
  const clientsFiltres = clients.filter(c => c.nom?.toLowerCase().includes(recherche.toLowerCase()));

  if (vue === 'carte') return <CarteClients isManager={false} user={user} onClose={() => setVue('accueil')} />;
  if (vue === 'planning') return <PlanningSemaine isManager={false} user={user} onClose={() => setVue('accueil')} />;
  if (showRecherche) return <RechercheGlobale user={user} isManager={false} onClose={() => setShowRecherche(false)} onSelectClient={(c) => { setShowRecherche(false); setFicheClient(c); }} />;

  if (showNotifs) return <Notifications user={user} isManager={false} onClose={() => { setShowNotifs(false); chargerNbNotifs(); }} />;
  if (ficheClient) return <FicheClient client={ficheClient} user={user} isManager={false} onClose={() => setFicheClient(null)} />;

  if (vue === 'profil') return <ProfilCommercial user={user} onLogout={onLogout} onClose={() => setVue('accueil')} />;

  if (showFormVisite && clientSelectionne) {
    return <FormulaireVisite user={user} client={clientSelectionne} onClose={() => { setShowFormVisite(false); setClientSelectionne(null); }} onSave={() => chargerDonnees()} />;
  }

  if (widget === 'objectif') return (
    <div style={styles.app}>
      <div style={{ ...styles.topbar, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <button onClick={() => setWidget(null)} style={styles.backBtn}>←</button>
        <span style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>Objectif du mois</span>
      </div>
      <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[
          { label: 'Visites réalisées', val: `${visites.length} / ${user.objectifVisites || 60}`, pct: score },
          { label: 'Taux qualité', val: '100%', pct: 100 },
          { label: 'Nouveaux clients', val: `${clients.filter(c => c.statut === 'Nouveau client').length} / ${user.objectifClients || 3}`, pct: Math.round(clients.filter(c => c.statut === 'Nouveau client').length / (user.objectifClients || 3) * 100) },
          { label: 'Score global', val: `${score}%`, pct: score },
        ].map((k, i) => (
          <div key={i} style={styles.kpiCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: '#666' }}>{k.label}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: k.pct >= 90 ? '#3B6D11' : k.pct >= 60 ? '#854F0B' : theme.couleur }}>{k.val}</span>
            </div>
            <div style={{ height: 5, background: '#f0f0f0', borderRadius: 99 }}>
              <div style={{ height: 5, background: k.pct >= 90 ? '#3B6D11' : k.pct >= 60 ? '#E67E22' : theme.couleur, borderRadius: 99, width: `${Math.min(k.pct, 100)}%` }}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (widget === 'actions') return (
    <div style={styles.app}>
      <div style={{ ...styles.topbar, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <button onClick={() => setWidget(null)} style={styles.backBtn}>←</button>
        <span style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>Actions prévues</span>
      </div>
      <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {actionsRetard.length > 0 && <>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#A32D2D', textTransform: 'uppercase' }}>⚠️ En retard</div>
          {actionsRetard.map((a, i) => (
            <div key={i} style={styles.actionCard}>
              <div style={styles.visitName}>{a.clientNom}</div>
              <div style={styles.visitSub}>{a.action} · {a.dateAction}</div>
            </div>
          ))}
        </>}
        {actionsAvenir.length > 0 && <>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#3B6D11', textTransform: 'uppercase' }}>✓ À venir</div>
          {actionsAvenir.map((a, i) => (
            <div key={i} style={styles.actionCard}>
              <div style={styles.visitName}>{a.clientNom}</div>
              <div style={styles.visitSub}>{a.action} · {a.dateAction}</div>
            </div>
          ))}
        </>}
        {actions.length === 0 && <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>Aucune action prévue</div>}
      </div>
    </div>
  );

  return (
    <div style={styles.app}>
      {/* TOPBAR */}
      <div style={{ ...styles.topbar, background: theme.couleur }}>
        <div>
          <div style={styles.topbarName}>Bonjour, {user.prenom || user.nom}</div>
          <div style={styles.topbarSub}>{today} · 📍 {wilaya}</div>
        </div>
        <button style={styles.themeBtn} onClick={() => setShowRecherche(true)}>🔍</button>
        <button style={styles.themeBtn} onClick={() => setShowNotifs(true)}>
          🔔{nbNotifs > 0 && <span style={styles.notifBadge}>{nbNotifs}</span>}
        </button>
        <button style={styles.themeBtn} onClick={() => setShowTheme(true)}>🎨</button>
        <div style={{...styles.avatar, cursor: 'pointer'}} onClick={() => setVue('profil')}>{(user.prenom || user.nom || 'U')[0].toUpperCase()}</div>
      </div>

      {/* CONTENU PAR VUE */}
      {vue === 'accueil' && (
        <div style={styles.widgets}>
          <div style={styles.widgetCard} onClick={() => setWidget('objectif')}>
            <div style={styles.widgetRow}>
              <div><div style={styles.widgetTitle}>Objectif du mois</div><div style={styles.widgetSub}>Mai 2026</div></div>
              <div style={{ fontSize: 22, fontWeight: 700, color: theme.couleur }}>{score}%</div>
            </div>
            <div style={styles.progressTrack}><div style={{ ...styles.progressFill, width: `${Math.min(score, 100)}%` }}></div></div>
          </div>

          <div style={styles.widgetCard} onClick={() => setVue('clients')}>
            <div style={styles.widgetRow}>
              <div><div style={styles.widgetTitle}>Planning du jour</div><div style={styles.widgetSub}>{visitesToday.length} visite(s) réalisée(s)</div></div>
              <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 99, background: '#EAF3DE', color: '#3B6D11' }}>{visitesToday.length} faite(s)</span>
            </div>
            <div style={{ marginTop: 10 }}>
              {clients.slice(0, 3).map((c, i) => (
                <div key={i} style={styles.visitPreviewRow}><span>{c.nom}</span><span style={{ color: '#854F0B', fontSize: 11 }}>→ Visiter</span></div>
              ))}
              {clients.length === 0 && <div style={{ fontSize: 12, color: '#aaa' }}>Aucun client — Ajoutez-en un !</div>}
            </div>
          </div>

          <div style={styles.widgetCard} onClick={() => setWidget('actions')}>
            <div style={styles.widgetRow}>
              <div><div style={styles.widgetTitle}>Actions prévues</div><div style={styles.widgetSub}>{actions.length} action(s)</div></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {actionsRetard.length > 0 && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#FCEBEB', color: '#A32D2D' }}>{actionsRetard.length} en retard</span>}
                {actionsAvenir.length > 0 && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#EAF3DE', color: '#3B6D11' }}>{actionsAvenir.length} à venir</span>}
              </div>
            </div>
          </div>



        </div>
      )}

      {vue === 'clients' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '10px 12px', background: '#fff', borderBottom: '1px solid #eee', display: 'flex', gap: 8 }}>
            <input style={{ ...styles.searchInput, flex: 1 }} placeholder="🔍 Rechercher un client..." value={recherche} onChange={e => setRecherche(e.target.value)} />
            <button style={{ padding: '9px 14px', background: COULEUR_DEFAULT, border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }} onClick={() => setShowFormClient(true)}>+ Client</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {clientsFiltres.map((c, i) => (
              <div key={i} style={styles.clientCard}>
                <div style={styles.clientHeader}>
                  <div style={styles.clientAv}>{c.nom?.[0]?.toUpperCase()}</div>
                  <div style={{ flex: 1 }}>
                    <div style={styles.visitName}>{c.nom}</div>
                    <div style={styles.visitSub}>{c.type || c.statut} · {c.commune}</div>
                  </div>
                  <button style={{ ...styles.saisirBtn, background: '#E6F1FB', color: '#185FA5', marginRight: 4 }} onClick={() => setFicheClient(c)}>👁</button>
                  <button style={styles.saisirBtn} onClick={() => { setClientSelectionne(c); setShowFormVisite(true); }}>
                    ✏️ Saisir
                  </button>
                </div>
                {c.telephone && <div style={{ fontSize: 12, color: '#888', padding: '4px 0 0 42px' }}>📞 {c.telephone}</div>}
              </div>
            ))}
            {clientsFiltres.length === 0 && (
              <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>
                <div style={{ fontSize: 32 }}>👥</div>
                <div style={{ marginTop: 8 }}>Aucun client trouvé</div>
              </div>
            )}
          </div>

        </div>
      )}

      {vue === 'visites' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', marginBottom: 8 }}>Historique des visites ({visites.length})</div>
          {visites.length === 0 && <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>Aucune visite enregistrée</div>}
          {visites.map((v, i) => (
            <div key={i} style={{ ...styles.clientCard, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={styles.visitName}>{v.clientNom}</div>
                  <div style={styles.visitSub}>{v.resultat} · {v.heureDebut ? new Date(v.heureDebut).toLocaleDateString('fr-FR') : ''}</div>
                  {v.commentaire && <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>💬 {v.commentaire}</div>}
                </div>
                <button style={{ background: '#FCEBEB', border: 'none', borderRadius: 6, color: '#A32D2D', fontSize: 11, cursor: 'pointer', padding: '4px 8px', flexShrink: 0 }}
                  onClick={() => setVisiteASupprimer(v)}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FORM NOUVEAU CLIENT */}
      {showFormClient && (
        <div style={styles.formOverlay}>
          <div style={styles.formModal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Nouveau client</div>
              <button onClick={() => setShowFormClient(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>
            {[
              { label: 'Nom *', key: 'nom', placeholder: 'Nom du client ou entreprise' },
              { label: 'Telephone', key: 'telephone', placeholder: '0X XX XX XX XX' },
              { label: 'Commune', key: 'commune', placeholder: 'Ex: Constantine' },
              { label: 'Wilaya', key: 'wilaya', placeholder: 'Ex: Constantine' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{f.label}</div>
                <input style={styles.formInput} placeholder={f.placeholder} value={nouveauClient[f.key]}
                  onChange={e => setNouveauClient(n => ({ ...n, [f.key]: e.target.value }))} />
              </div>
            ))}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Type</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['GROSSISTE', 'DEMI GROS', 'DETAILLANT', 'DECORATIVE', 'ENTREPRENEUR'].map(tp => (
                  <button key={tp} style={{ ...styles.choiceSmall, ...(nouveauClient.type === tp ? styles.choiceSmallActive : {}) }}
                    onClick={() => setNouveauClient(n => ({ ...n, type: tp }))}>{tp}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Statut</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['Prospect', 'Client actif', 'Nouveau client', 'Client inactif'].map(s => (
                  <button key={s} style={{ ...styles.choiceSmall, ...(nouveauClient.statut === s ? styles.choiceSmallActive : {}) }}
                    onClick={() => setNouveauClient(n => ({ ...n, statut: s }))}>{s}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Potentiel</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {['Faible', 'Moyen', 'Fort'].map(p => (
                  <button key={p} style={{ ...styles.choiceSmall, ...(nouveauClient.potentiel === p ? styles.choiceSmallActive : {}) }}
                    onClick={() => setNouveauClient(n => ({ ...n, potentiel: p }))}>{p}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Position GPS (optionnel)</div>
              <input style={styles.formInput} placeholder="Ex: 36.3650, 6.6147"
                value={nouveauClient.gpsText}
                onChange={e => setNouveauClient(n => ({ ...n, gpsText: e.target.value }))} />
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>Clic droit sur Google Maps puis Copier les coordonnees</div>
            </div>
            <button style={{ ...styles.addClientBtn, opacity: nouveauClient.nom ? 1 : 0.4 }}
              onClick={ajouterClient} disabled={loadingClient || !nouveauClient.nom}>
              {loadingClient ? 'Enregistrement...' : '✅ Ajouter le client'}
            </button>
          </div>
        </div>
      )}

      {visiteASupprimer && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: '#fff', width: '100%', maxWidth: 480, margin: '0 auto', borderRadius: '20px 20px 0 0', padding: '20px 16px 30px' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1C2833', marginBottom: 10 }}>🗑 Supprimer cette visite ?</div>
            <div style={{ fontSize: 13, color: '#444', marginBottom: 6 }}>Client : <strong>{visiteASupprimer.clientNom}</strong></div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>{visiteASupprimer.heureDebut ? new Date(visiteASupprimer.heureDebut).toLocaleDateString('fr-FR') : ''} · {visiteASupprimer.resultat}</div>
            <div style={{ fontSize: 12, color: '#A32D2D', background: '#FCEBEB', padding: '8px 12px', borderRadius: 8, marginBottom: 16 }}>⚠️ Action irréversible</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ flex: 1, padding: 11, border: '1px solid #ddd', borderRadius: 10, background: '#fff', color: '#666', fontSize: 14, cursor: 'pointer' }} onClick={() => setVisiteASupprimer(null)}>Annuler</button>
              <button style={{ flex: 2, padding: 11, background: '#C0392B', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }} onClick={() => supprimerVisite(visiteASupprimer.id)}>🗑 Confirmer</button>
            </div>
          </div>
        </div>
      )}
      {showTheme && <SelecteurTheme onClose={() => setShowTheme(false)} />}

      {/* NAVBAR */}
      <div style={styles.navbar}>
        {[
          { id: 'accueil', icon: '🏠', label: 'Accueil' },
          { id: 'clients', icon: '👥', label: 'Clients' },
          { id: 'visites', icon: '📋', label: 'Visites' },
          { id: 'carte', icon: '🗺️', label: 'Carte', action: () => setVue('carte') },
          { id: 'planning', icon: '📅', label: 'Planning', action: () => setVue('planning') },
        ].map(nav => (
          <div key={nav.id} style={{ ...styles.navItem, color: vue === nav.id ? theme.couleur : '#999' }} onClick={() => { setWidget(null); setVue(nav.id); }}>
            <span style={{ fontSize: 20 }}>{nav.icon}</span>
            <span style={{ fontSize: 10 }}>{nav.label}</span>
          </div>
        ))}

      </div>
    </div>
  );
}

const styles = {
  app: { maxWidth: 480, margin: '0 auto', height: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif', overflow: 'hidden' },
  topbar: { background: COULEUR_DEFAULT, padding: '1rem 1.25rem', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  topbarName: { fontSize: 15, fontWeight: 600 },
  topbarSub: { fontSize: 12, opacity: 0.8, marginTop: 2 },
  avatar: { width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: '#fff' },
  backBtn: { background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer' },
  widgets: { flex: 1, display: 'flex', flexDirection: 'column', gap: 8, padding: 8 },
  widgetCard: { background: '#fff', borderRadius: 12, padding: '1rem 1.25rem', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  widgetRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  widgetTitle: { fontSize: 14, fontWeight: 600, color: '#1C2833' },
  widgetSub: { fontSize: 12, color: '#888', marginTop: 3 },
  progressTrack: { height: 5, background: '#f0f0f0', borderRadius: 99, marginTop: 8 },
  progressFill: { height: 5, background: COULEUR_DEFAULT, borderRadius: 99, transition: 'width 0.5s' },
  visitPreviewRow: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#666', marginTop: 5 },
  kpiCard: { background: '#fff', borderRadius: 10, padding: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  actionCard: { background: '#fff', borderRadius: 10, padding: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  searchInput: { width: '100%', padding: '9px 12px', border: '1px solid #eee', borderRadius: 8, fontSize: 14, background: '#f9f9f9', fontFamily: 'system-ui' },
  clientCard: { background: '#fff', borderRadius: 10, padding: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  clientHeader: { display: 'flex', alignItems: 'center', gap: 10 },
  clientAv: { width: 36, height: 36, borderRadius: '50%', background: '#FCEBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: COULEUR_DEFAULT, flexShrink: 0 },
  visitName: { fontSize: 13, fontWeight: 600, color: '#1C2833' },
  visitSub: { fontSize: 11, color: '#888', marginTop: 2 },
  saisirBtn: { padding: '7px 12px', background: COULEUR_DEFAULT, border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 },
  addClientBtn: { width: '100%', padding: 12, background: COULEUR_DEFAULT, border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  formOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50, display: 'flex', alignItems: 'flex-end' },
  formModal: { background: '#fff', width: '100%', maxWidth: 480, margin: '0 auto', borderRadius: '20px 20px 0 0', padding: '20px 16px 30px', maxHeight: '85vh', overflowY: 'auto' },
  formInput: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontFamily: 'system-ui' },
  choiceSmall: { padding: '6px 12px', border: '1px solid #ddd', borderRadius: 99, background: '#fff', color: '#666', fontSize: 12, cursor: 'pointer' },
  choiceSmallActive: { border: `1px solid ${COULEUR_DEFAULT}`, background: '#FCEBEB', color: COULEUR_DEFAULT, fontWeight: 600 },
  notifBadge: { position: 'absolute', top: -4, right: -4, background: '#E74C3C', color: '#fff', fontSize: 9, width: 14, height: 14, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 },
  themeBtn: { background: 'rgba(255,255,255,0.2)', position: 'relative', border: 'none', borderRadius: 8, width: 32, height: 32, fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  navbar: { display: 'flex', background: '#fff', borderTop: '1px solid #eee', padding: '8px 0 4px' },
  navItem: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 10 },
};
